import { ApiClient } from './client';
import type { Ontology, OntologyNode, OntologyEdge, ConnectNodesRequest, ConnectNodesResponse, FindSimilarNodesRequest, FindSimilarNodesResponse } from '@shared/types/ontology';

/**
 * API for AI-powered ontology generation
 */

export interface GenerateOntologyRequest {
  domain: string;
  nodeCount?: number;
}

export interface GenerateOntologyResponse {
  ontology: Ontology;
  nodes: OntologyNode[];
  edges: OntologyEdge[];
}

export interface ExpandConceptRequest {
  concept: {
    id: string;
    label: string;
    definition: string;
    properties?: string[];
  };
  linkedConcepts?: Array<{
    label: string;
    definition: string;
    relationship: string;
  }>;
  allNodes?: Array<{ id: string; label: string; definition?: string }>;
  relationshipContext?: string;
  maxSuggestions?: number;
}

export interface ExpandedConcept {
  label: string;
  definition: string;
  properties: string[];
  relationship: string;
}

export interface ExpandConceptResponse {
  suggestions: ExpandedConcept[];
  explanation: string;
}

// Use a longer timeout for ontology generation (5 min) since AI generation can take a while
const ontologyApiClient = new ApiClient({ timeout: 300000 });

// Single-shot LLM call (not a background job) — 60s is sufficient
const connectNodesApiClient = new ApiClient({ timeout: 60000 });

export const ontologyGenerationApi = {
  /**
   * Generate an ontology structure from a domain description
   * Uses AI to create initial nodes and relationships
   */
  async generateFromPrompt(request: GenerateOntologyRequest): Promise<GenerateOntologyResponse> {
    const response: any = await ontologyApiClient.post('/api/ontologies/generate', {
      domain: request.domain,
      nodeCount: request.nodeCount || 20,
    });
    
    // apiClient already extracts the `data` field from { success: true, data: {...} }
    // So response is directly { ontology, nodes, edges }
    return response;
  },

  /**
   * Expand a concept with AI-generated related suggestions
   * Returns new concepts that can be added to the graph
   */
  async expandConcept(request: ExpandConceptRequest): Promise<ExpandConceptResponse> {
    const response: any = await ontologyApiClient.post('/api/ontologies/expand-concept', {
      concept: request.concept,
      linkedConcepts: request.linkedConcepts,
      allNodes: request.allNodes,
      relationshipContext: request.relationshipContext,
      maxSuggestions: request.maxSuggestions || 10,
    });

    return response;
  },

  /**
   * Propose relationships to connect isolated (disconnected) nodes into the
   * rest of the graph, using AI.
   */
  async connectNodes(request: ConnectNodesRequest): Promise<ConnectNodesResponse> {
    const response: any = await connectNodesApiClient.post('/api/ontologies/connect-nodes', {
      isolatedNodes: request.isolatedNodes,
      graphNodes: request.graphNodes,
      graphEdges: request.graphEdges,
      maxSuggestionsPerNode: request.maxSuggestionsPerNode,
    });

    return response;
  },

  /**
   * Find semantically similar (potentially duplicate) node pairs in the graph,
   * using AI. The user can then choose which nodes to remove.
   */
  async findSimilarNodes(
    request: Omit<FindSimilarNodesRequest, 'userId'>
  ): Promise<FindSimilarNodesResponse> {
    const response: any = await connectNodesApiClient.post('/api/ontologies/find-similar-nodes', {
      nodes: request.nodes,
    });
    return response;
  },
};

// Made with Bob
